import { ref, computed, watch, onMounted, readonly } from 'vue'
import type { UFPMMember, UFPMFilter, UFPMImportResult, UFPMScoreEntry, UFPMSubTab } from '../lib/types'
import {
  normalizeStoredMember, getNowId, getFilteredMembers, mergeImportedMembers,
  sortMembers, getDueMonthKey, dueMonthLabel, parseRosterFile,
} from '../lib/ufpmHelpers'
import { hasUfpmTestSessionDrafts, readUfpmModeEnabled, UFPM_MODE_KEY } from '../lib/ufpmMode'
import { clearSession, createEmptySession } from '../lib/testSession'

const STORAGE_KEY  = 'ufpm_members'
const MODE_KEY     = UFPM_MODE_KEY
const SELECTED_KEY = 'ufpm_selected_member'

function dispatchMembersChanged(members: UFPMMember[]): void {
  try {
    window.dispatchEvent(new CustomEvent('ptr:ufpm-members-changed', {
      detail: { members },
    }))
  } catch { /* ignore */ }
}

function dispatchModeChanged(enabled: boolean, source: 'toggle' | 'cloud-restore' | 'render' = 'render'): void {
  window.dispatchEvent(new CustomEvent('ptr:ufpm-mode-changed', {
    detail: { enabled, signedIn: !!window.authService?.currentUser, source },
  }))
}

export function useUFPM() {
  const members          = ref<UFPMMember[]>([])
  const selectedMemberId = ref<string | null>(null)
  const search           = ref('')
  const filters          = ref<UFPMFilter>({ fitnessCurrency: '', assessmentCategory: '', dueMonth: '' })
  const subTab           = ref<UFPMSubTab>('overview')
  const modeEnabled      = ref(false)
  const uploadError      = ref('')
  const isUploading      = ref(false)
  const editingMemberId  = ref<string | null>(null)
  const showMemberPicker = ref(false)
  const pickerSource     = ref<'ufpm' | 'calculator' | null>(null)

  // ---- computed ----

  const filteredMembers = computed(() =>
    getFilteredMembers(members.value, search.value, filters.value)
  )
  const selectedMember = computed(() =>
    members.value.find(m => m.id === selectedMemberId.value) ?? null
  )
  const dashboardMetrics = computed(() => {
    const scores = members.value
      .map(m => Number(m.history?.[0]?.composite || m.compositeScore))
      .filter(s => Number.isFinite(s))
    const now        = new Date()
    const thirtyDays = new Date(now.getTime() + 30 * 86400000)
    const dueSoon = members.value.filter(m => {
      if (!m.nextFitnessAssessmentDueDate) return false
      const due = new Date(m.nextFitnessAssessmentDueDate + 'T00:00:00')
      return !isNaN(due.getTime()) && due >= new Date(now.toDateString()) && due <= thirtyDays
    }).length
    return {
      total:    members.value.length,
      tested:   scores.length,
      avgScore: scores.length
        ? (scores.reduce((s, v) => s + v, 0) / scores.length).toFixed(1)
        : '-',
      dueSoon,
    }
  })
  const uniqueCurrencies = computed(() =>
    [...new Set(members.value.map(m => m.fitnessCurrency).filter(Boolean))].sort()
  )
  const uniqueCategories = computed(() =>
    [...new Set(members.value.map(m => m.latestFitnessAssessmentCategory).filter(Boolean))].sort()
  )
  const dueMonthOptions = computed(() => {
    const keys = [...new Set(
      members.value.map(m => getDueMonthKey(m.nextFitnessAssessmentDueDate)).filter(Boolean)
    )].sort()
    return keys.map(k => ({ value: k, label: dueMonthLabel(k) }))
  })

  // ---- storage ----

  function loadFromStorage(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      const parsed: unknown[] = raw ? JSON.parse(raw) : []
      members.value = (Array.isArray(parsed) ? parsed : [])
        .map(normalizeStoredMember)
        .filter((m): m is UFPMMember => m !== null)
      const saved = localStorage.getItem(SELECTED_KEY)
      selectedMemberId.value = (saved && members.value.some(m => m.id === saved))
        ? saved
        : (members.value[0]?.id ?? null)
    } catch (_) {
      members.value = []
      selectedMemberId.value = null
    }
  }

  function saveToStorage(): void {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(members.value)) } catch (_) { /* quota */ }
    try { localStorage.setItem(SELECTED_KEY, selectedMemberId.value || '') }  catch (_) { /* quota */ }
    dispatchMembersChanged(members.value)
  }

  // ---- member actions ----

  function selectMember(id: string): void {
    selectedMemberId.value = id
    subTab.value = 'overview'
    try { localStorage.setItem(SELECTED_KEY, id) } catch (_) { /* ignore */ }
  }

  function deleteMember(id: string, ask = true): void {
    const member = members.value.find(m => m.id === id)
    if (!member) return
    if (ask && !window.confirm('Remove ' + member.fullName + ' from UFPM roster?')) return
    members.value = members.value.filter(m => m.id !== id)
    if (selectedMemberId.value === id) {
      selectedMemberId.value = members.value[0]?.id ?? null
      try { localStorage.setItem(SELECTED_KEY, selectedMemberId.value || '') } catch (_) { /* ignore */ }
    }
    saveToStorage()
    window.showToast?.('Member removed')
  }

  function deleteSelectedMember(): void {
    if (selectedMemberId.value) deleteMember(selectedMemberId.value, true)
  }

  function clearRoster(): void {
    if (!members.value.length) { window.showToast?.('Roster is already empty.'); return }
    if (!window.confirm(
      'Clear the entire UFPM roster? This permanently removes all members, their score histories, saved plans, and any active test session queue. This cannot be undone.'
    )) return
    members.value          = []
    selectedMemberId.value = null
    search.value           = ''
    filters.value          = { fitnessCurrency: '', assessmentCategory: '', dueMonth: '' }
    clearSession()
    try {
      window.dispatchEvent(new CustomEvent('ptr:ufpm-session-changed', {
        detail: { session: createEmptySession() },
      }))
    } catch { /* ignore */ }
    saveToStorage()
    window.showToast?.('UFPM roster cleared')
  }

  function submitMemberForm(data: Partial<UFPMMember>): void {
    const now      = Date.now()
    const existing = data.id ? members.value.find(m => m.id === data.id) : null
    const member   = normalizeStoredMember({
      ...(existing || {}),
      ...data,
      id:         existing?.id || getNowId('member'),
      importedAt: existing?.importedAt || now,
      updatedAt:  now,
    })
    if (!member || (!member.firstName && !member.lastName)) {
      window.showToast?.('Enter first and last name.')
      return
    }
    if (existing) {
      const idx = members.value.findIndex(m => m.id === existing.id)
      members.value[idx] = member
    } else {
      members.value.push(member)
    }
    members.value.sort(sortMembers)
    selectedMemberId.value = member.id
    editingMemberId.value  = null
    saveToStorage()
    window.showToast?.(existing ? 'Member updated' : 'Member added')
  }

  // ---- mode ----

  function setModeEnabled(enabled: boolean): void {
    if (!enabled && hasUfpmTestSessionDrafts()) {
      const proceed = window.confirm(
        'Discard the active test session? Unsaved draft scores will be lost. Saved scores are kept.'
      )
      if (!proceed) {
        dispatchModeChanged(true, 'render')
        return
      }
      try { sessionStorage.removeItem('ufpm_test_session') } catch (_) { /* ignore */ }
    }
    modeEnabled.value = enabled
    try { localStorage.setItem(MODE_KEY, enabled ? 'true' : 'false') } catch (_) { /* ignore */ }
    dispatchModeChanged(enabled, 'toggle')
    const user = window.authService?.currentUser
    if (user && window.syncService?.syncUserSettings) {
      window.syncService.syncUserSettings(user.uid, { ufpmMode: !!enabled }).catch((e: unknown) => {
        console.warn('[ufpm] Could not sync UFPM mode preference:', e)
      })
    }
    window.showToast?.(enabled ? 'UFPM Mode enabled' : 'UFPM Mode disabled')
  }

  function toggleMode(): void { setModeEnabled(!modeEnabled.value) }

  // ---- score / plan bridging ----

  function saveScoreForSelectedMember(score: UFPMScoreEntry): boolean {
    if (!selectedMember.value || !score) return false
    const member = selectedMember.value
    const entry  = { ...score, id: score.id || Date.now(), memberId: member.id, memberName: member.fullName }
    const idx    = members.value.findIndex(m => m.id === member.id)
    if (idx === -1) return false
    const e = entry as Record<string, unknown>
    const lastTestDate =
      (e.recordedDate as string | undefined) ||
      (e.officialTestDate as string | undefined) ||
      (e.testDate as string | undefined) ||
      member.lastTestDate || null
    members.value[idx] = {
      ...member,
      history:  [entry, ...(member.history || [])].slice(0, 100),
      lastTestDate,
      latestFitnessAssessmentCompletion: lastTestDate,
      sex:      (entry.sex  || member.sex  || '') as UFPMMember['sex'],
      age:      entry.age  || member.age  || 0,
      compositeScore:                  String(entry.composite || member.compositeScore || ''),
      latestFitnessAssessmentCategory: entry.cat || member.latestFitnessAssessmentCategory || '',
      updatedAt: Date.now(),
    }
    saveToStorage()
    return true
  }

  function savePlanForSelectedMember(plan: unknown): boolean {
    if (!selectedMember.value || !plan) return false
    return savePlanForMember(selectedMember.value.id, plan)
  }

  function savePlanForMember(memberId: string, plan: unknown): boolean {
    if (!memberId || !plan) return false
    const member = members.value.find(m => m.id === memberId)
    if (!member) return false
    const idx = members.value.findIndex(m => m.id === memberId)
    if (idx === -1) return false
    const planEntry = {
      id:            getNowId('plan'),
      createdAt:     Date.now(),
      sourceScoreId: window.lastScore ? String(((window.lastScore as unknown) as Record<string, unknown>).id ?? '') : null,
      plan:          plan as Record<string, unknown>,
    }
    members.value[idx] = {
      ...member,
      plans:     [planEntry, ...(member.plans || [])].slice(0, 25),
      updatedAt: Date.now(),
    }
    saveToStorage()
    return true
  }

  // ---- upload ----

  async function handleUpload(file: File): Promise<void> {
    isUploading.value = true
    uploadError.value = ''
    const startedAt = Date.now()
    const minLoadingMs = 750
    try {
      const result: UFPMImportResult = await parseRosterFile(file)
      const { members: merged, added, updated } = mergeImportedMembers(members.value, result.members)
      members.value = merged
      if (!selectedMemberId.value && members.value.length) {
        selectedMemberId.value = members.value[0]?.id ?? null
      }
      saveToStorage()
      const missing = result.missingHeaders.length
        ? ' Missing headers: ' + result.missingHeaders.join(', ') + '.'
        : ''
      window.showToast?.('Roster imported: ' + added + ' added, ' + updated + ' updated.' + missing)
    } catch (e: unknown) {
      uploadError.value = e instanceof Error ? e.message : 'Roster import failed.'
      window.showToast?.(uploadError.value)
    } finally {
      const elapsed = Date.now() - startedAt
      if (elapsed < minLoadingMs) {
        await new Promise(resolve => setTimeout(resolve, minLoadingMs - elapsed))
      }
      isUploading.value = false
    }
  }

  // ---- cloud sync ----

  async function applyCloudModePreference(user: { uid: string }): Promise<void> {
    if (!user || !window.syncService?.getUserSettings) return
    try {
      const settings = await window.syncService.getUserSettings(user.uid)
      if (Object.prototype.hasOwnProperty.call(settings, 'ufpmMode')) {
        modeEnabled.value = !!settings.ufpmMode
        try { localStorage.setItem(MODE_KEY, modeEnabled.value ? 'true' : 'false') } catch (_) { /* ignore */ }
        dispatchModeChanged(modeEnabled.value, 'cloud-restore')
      } else if (modeEnabled.value && window.syncService?.syncUserSettings) {
        await window.syncService.syncUserSettings(user.uid, { ufpmMode: true })
      }
    } catch (e) {
      console.warn('[ufpm] Could not load UFPM mode preference:', e)
    }
  }

  // ---- onboarding demo roster ----

  const demoMemberIds = new Set<string>()

  async function importOnboardingDemoRoster(): Promise<void> {
    const response = await fetch('assets/onboarding/ufpm-demo-roster.xlsx')
    if (!response.ok) throw new Error('Could not load demo roster.')
    const blob = await response.blob()
    const file = new File([blob], 'ufpm-demo-roster.xlsx', { type: blob.type })
    const result: UFPMImportResult = await parseRosterFile(file)
    const existingIds = new Set(members.value.map(m => m.id))
    const tagged = result.members.map(m => ({ ...m, _onboardingDemo: true as const }))
    const { members: merged } = mergeImportedMembers(members.value, tagged)
    merged.filter(m => !existingIds.has(m.id)).forEach(m => demoMemberIds.add(m.id))
    members.value = merged
    if (!selectedMemberId.value && members.value.length) {
      selectedMemberId.value = members.value[0]?.id ?? null
    }
    saveToStorage()
  }

  function clearOnboardingDemoRoster(): void {
    const before = members.value.length
    members.value = members.value.filter(m => !demoMemberIds.has(m.id) && !m._onboardingDemo)
    demoMemberIds.clear()
    if (before !== members.value.length) {
      if (!members.value.find(m => m.id === selectedMemberId.value)) {
        selectedMemberId.value = members.value[0]?.id ?? null
      }
      saveToStorage()
    }
    try {
      clearSession()
      sessionStorage.removeItem('ufpm_test_session')
      window.dispatchEvent(new CustomEvent('ptr:ufpm-session-changed', {
        detail: { session: createEmptySession() },
      }))
    } catch { /* ignore */ }
  }

  // ---- tab navigation bridges ----

  function switchToCalculatorForMember(): void {
    if (typeof window.switchTab === 'function') window.switchTab('calc', document.getElementById('tab-calc'))
  }

  function switchToPlanForMember(): void {
    if (typeof window.switchTab === 'function') window.switchTab('training', document.getElementById('tab-training'))
  }

  function openMemberPickerForCalculator(): void {
    pickerSource.value = 'calculator'
    showMemberPicker.value = true
    // panel-ufpm is now a persistent tab island — no forced tab switch needed
  }

  // ---- watchers (cross-tab sync) ----

  watch(selectedMemberId, () => {
    window.dispatchEvent(new CustomEvent('ptr:ufpm-member-selected', {
      detail: { member: selectedMember.value },
    }))
  })

  // ---- mount: set window.ufpmTools bridge ----

  onMounted(() => {
    modeEnabled.value = readUfpmModeEnabled()
    loadFromStorage()
    dispatchMembersChanged(members.value)

    window.ufpmTools = {
      isModeEnabled:              () => modeEnabled.value,
      setModeEnabled,
      toggleMode,
      applyCloudModePreference,
      loadMembers:                loadFromStorage,
      reloadFromStorage:          loadFromStorage,
      getMembers:                 () => members.value,
      render: () => {
        // app.js calls render() on auth state changes; re-sync mode from localStorage
        const stored = readUfpmModeEnabled()
        modeEnabled.value = stored
        dispatchModeChanged(stored, 'render')
      },
      selectMember,
      getSelectedMember:          () => selectedMember.value,
      saveScoreForSelectedMember,
      savePlanForSelectedMember,
      savePlanForMember,
      editSelectedMember:         () => { editingMemberId.value = selectedMemberId.value },
      deleteMember,
      deleteSelectedMember,
      clearRoster,
      switchToCalculatorForMember,
      switchToPlanForMember,
      openMemberPickerForCalculator,
      importOnboardingDemoRoster,
      clearOnboardingDemoRoster,
    }

    // Initial cross-tab sync (shim picks these up on the very first load)
    dispatchModeChanged(modeEnabled.value, 'render')
    window.dispatchEvent(new CustomEvent('ptr:ufpm-member-selected', {
      detail: { member: selectedMember.value },
    }))
  })

  return {
    // writable by template
    search, filters, subTab, editingMemberId,
    showMemberPicker, pickerSource,
    // readonly state
    members:           readonly(members),
    selectedMemberId:  readonly(selectedMemberId),
    modeEnabled:       readonly(modeEnabled),
    uploadError:       readonly(uploadError),
    isUploading:       readonly(isUploading),
    // computed
    filteredMembers, selectedMember, dashboardMetrics,
    uniqueCurrencies, uniqueCategories, dueMonthOptions,
    // actions
    loadFromStorage, selectMember, deleteMember, deleteSelectedMember, clearRoster,
    submitMemberForm, setModeEnabled, toggleMode,
    saveScoreForSelectedMember, savePlanForSelectedMember, savePlanForMember,
    handleUpload, applyCloudModePreference,
    switchToCalculatorForMember, switchToPlanForMember,
    openMemberPickerForCalculator,
    importOnboardingDemoRoster, clearOnboardingDemoRoster,
    closeMemberPicker: () => { showMemberPicker.value = false; pickerSource.value = null },
  }
}
